import { Chart, Plugin } from "chart.js";

const DEFAULT_COLOR = "rgba(0, 120, 200, 0.9)";
const DEFAULT_WIDTH = 1;
const DASH: number[] = [4, 3];

export interface IFaultFrequencyCursorItem {
  frequency: number;
  lineColor?: string;
}

export interface IFaultFrequenciesCursorsOptions {
  enabled?: boolean;
  frequencies?: number[];
  cursors?: IFaultFrequencyCursorItem[];
  xOffset?: number;
  lineColor?: string;
  lineWidth?: number;
  dashPattern?: number[];
}

type ChartScalesY = { min: number; max: number; getPixelForValue: (v: number) => number } | undefined;

export const FaultFrequenciesCursorsPlugin: Plugin<"line"> = {
  id: "faultFrequenciesCursors",

  afterDraw(chartInstance) {
    const chart = chartInstance as Chart<"line">;
    const pluginOptions = (chart.options as { plugins?: { faultFrequenciesCursors?: IFaultFrequenciesCursorsOptions } })
      .plugins;
    const opts = pluginOptions?.faultFrequenciesCursors;
    if (!opts || opts.enabled !== true) {
      return;
    }

    const cursors: IFaultFrequencyCursorItem[] = opts.cursors?.length
      ? opts.cursors
      : (opts.frequencies ?? []).map(frequency => ({ frequency }));

    if (!cursors.length) {
      return;
    }

    const xScale = chart.scales.x;
    const meta0 = chart.getDatasetMeta(0) as { yAxisID?: string } | undefined;
    const yAxisId = meta0?.yAxisID;
    const yScale = (yAxisId ? (chart.scales as Record<string, ChartScalesY>)[yAxisId] : chart.scales.y) as ChartScalesY;
    if (!xScale || !yScale || typeof xScale.getPixelForValue !== "function") {
      return;
    }

    const min = xScale.min;
    const max = xScale.max;
    if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
      return;
    }

    const {
      xOffset = 0,
      lineColor: defaultLineColor = DEFAULT_COLOR,
      lineWidth = DEFAULT_WIDTH,
      dashPattern = DASH,
    } = opts;

    const datasets = chart.data.datasets;
    const is3D = datasets.length > 1 && xOffset !== 0;
    const ctx = chart.ctx;

    const drawVLine = (xPix: number, strokeColor: string) => {
      ctx.save();
      ctx.setLineDash(dashPattern);
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = strokeColor;
      ctx.beginPath();
      ctx.moveTo(xPix, yScale.getPixelForValue(yScale.max));
      ctx.lineTo(xPix, yScale.getPixelForValue(yScale.min));
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    };

    for (const { frequency: rawFk, lineColor: cursorColor } of cursors) {
      if (typeof rawFk !== "number" || !Number.isFinite(rawFk) || rawFk <= 0) {
        continue;
      }

      const strokeColor = cursorColor ?? defaultLineColor;

      if (!is3D) {
        if (rawFk < min || rawFk > max) {
          continue;
        }
        drawVLine(xScale.getPixelForValue(rawFk), strokeColor);
        continue;
      }

      for (let dsIndex = 0; dsIndex < datasets.length; dsIndex++) {
        const expectedX = rawFk + dsIndex * xOffset;
        if (expectedX < min || expectedX > max) {
          continue;
        }
        const ds = datasets[dsIndex];
        const points = ds.data as { x: number; y: number }[];
        if (!points.length) {
          continue;
        }
        let best = points[0];
        let bestDiff = Math.abs(points[0].x - expectedX);
        for (const pt of points) {
          if (pt && typeof pt.x === "number") {
            const d = Math.abs(pt.x - expectedX);
            if (d < bestDiff) {
              best = pt;
              bestDiff = d;
            }
          }
        }
        if (!best || typeof best.x !== "number") {
          continue;
        }
        drawVLine(xScale.getPixelForValue(best.x), strokeColor);
      }
    }
  },
};

export default FaultFrequenciesCursorsPlugin;
